import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { endShift } from '../lib/api';

const CARRY_REASONS = [
  { value: 'WAITING_ON_GUEST', label: 'Waiting on guest' },
  { value: 'WAITING_ON_VENDOR', label: 'Waiting on vendor' },
  { value: 'WAITING_ON_TEAM', label: 'Waiting on team' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'NEEDS_APPROVAL', label: 'Needs approval' },
  { value: 'NOT_STARTED', label: 'Not started' },
  { value: 'OTHER', label: 'Other' },
];

const PRIORITY_COLORS = {
  HANDLE_NOW: 'bg-red-50 text-red-700 border-red-200',
  DUE_TODAY: 'bg-amber-50 text-amber-700 border-amber-200',
  WAITING_ON: 'bg-blue-50 text-blue-700 border-blue-200',
  SCHEDULED: 'bg-stone-50 text-stone-600 border-stone-200',
};

// Tomorrow 9:00 AM local, formatted for <input type="datetime-local">
const defaultNextTouchLocal = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const EndShiftModal = ({ open, onOpenChange, unfinishedTasks = [], onShiftClosed }) => {
  // Filter to only active, unclosed tasks (defensive)
  const activeTasks = useMemo(
    () => (unfinishedTasks || []).filter(t => !t.closed_at && t.status !== 'DONE'),
    [unfinishedTasks]
  );

  const [carryOvers, setCarryOvers] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Pre-fill defaults whenever the modal opens or task list changes
  useEffect(() => {
    if (!open) return;
    const nextTouch = defaultNextTouchLocal();
    setCarryOvers(prev => {
      const seed = {};
      activeTasks.forEach(t => {
        seed[t.id] = {
          carry_over_reason: prev[t.id]?.carry_over_reason || t.carry_over_reason || 'SCHEDULED',
          next_touch_at: prev[t.id]?.next_touch_at || nextTouch,
        };
      });
      return seed;
    });
    setErrors({});
  }, [open, activeTasks]);

  const updateTask = (taskId, field, value) => {
    setCarryOvers(prev => ({
      ...prev,
      [taskId]: { ...prev[taskId], [field]: value },
    }));
    setErrors(prev => {
      const next = { ...prev };
      if (next[taskId]) {
        next[taskId] = { ...next[taskId], [field]: undefined };
      }
      return next;
    });
  };

  const validate = () => {
    const e = {};
    activeTasks.forEach(t => {
      const co = carryOvers[t.id] || {};
      const row = {};
      if (!co.carry_over_reason) row.carry_over_reason = 'Pick a reason';
      if (!co.next_touch_at) row.next_touch_at = 'Set a next touch';
      if (Object.keys(row).length) e[t.id] = row;
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleClose = async () => {
    if (!validate()) {
      toast.error('A few tasks need a reason and next touch.');
      return;
    }
    setSaving(true);
    try {
      const carry_overs = activeTasks.map(t => ({
        task_id: t.id,
        carry_over_reason: carryOvers[t.id]?.carry_over_reason || 'SCHEDULED',
        next_touch_at: new Date(carryOvers[t.id]?.next_touch_at || defaultNextTouchLocal()).toISOString(),
      }));
      const res = await endShift({ carry_overs });
      const count = res?.data?.updatedTasksCount ?? carry_overs.length;
      if (count > 0) {
        toast.success('Shift closed. Unfinished items moved to carry-over.', {
          description: `${count} task${count === 1 ? '' : 's'} carried over.`,
        });
      } else {
        toast.success('Shift closed. Clean handoff — nothing carried over.');
      }
      onShiftClosed?.(res?.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message;
      toast.error(`Couldn't close shift${detail ? ` — ${detail}` : ''}. Try again.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh]" data-testid="end-shift-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Close the shift</DialogTitle>
          <DialogDescription>
            {activeTasks.length === 0
              ? 'Everything is closed out. End your shift with a clean handoff.'
              : 'Anything unfinished will carry over with a reason and a next touch time. We\u2019ve pre-filled sensible defaults.'}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[50vh] pr-2">
          <div className="space-y-3 py-2">
            {activeTasks.length === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-stone-700 font-medium">Clean shift — nothing to carry over.</p>
                <p className="text-xs text-stone-500 mt-1">Click Close shift to finish.</p>
              </div>
            ) : (
              activeTasks.map(task => {
                const rowErrors = errors[task.id] || {};
                const reasonInvalid = !!rowErrors.carry_over_reason;
                const touchInvalid = !!rowErrors.next_touch_at;
                return (
                  <div
                    key={task.id}
                    className={`p-3 rounded-lg border space-y-2 ${
                      reasonInvalid || touchInvalid
                        ? 'bg-red-50/50 border-red-200'
                        : 'bg-stone-50 border-stone-100'
                    }`}
                    data-testid={`carry-over-task-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-900 truncate">{task.summary}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${PRIORITY_COLORS[task.priority] || ''}`}>
                            {task.priority?.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-[10px] text-stone-400">{task.category?.replace(/_/g, ' ')}</span>
                        </div>
                      </div>
                      <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Carry-over reason</Label>
                        <Select
                          value={carryOvers[task.id]?.carry_over_reason || ''}
                          onValueChange={v => updateTask(task.id, 'carry_over_reason', v)}
                        >
                          <SelectTrigger
                            className={`h-8 text-xs ${reasonInvalid ? 'border-red-400 ring-1 ring-red-200' : ''}`}
                            data-testid={`co-reason-${task.id}`}
                          >
                            <SelectValue placeholder="Select reason" />
                          </SelectTrigger>
                          <SelectContent>
                            {CARRY_REASONS.map(r => (
                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {reasonInvalid && (
                          <p className="text-[10px] text-red-600">{rowErrors.carry_over_reason}</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] flex items-center gap-1"><Clock className="h-3 w-3" /> Next touch</Label>
                        <Input
                          type="datetime-local"
                          value={carryOvers[task.id]?.next_touch_at || ''}
                          onChange={e => updateTask(task.id, 'next_touch_at', e.target.value)}
                          className={`h-8 text-xs ${touchInvalid ? 'border-red-400 ring-1 ring-red-200' : ''}`}
                          data-testid={`co-next-touch-${task.id}`}
                        />
                        {touchInvalid && (
                          <p className="text-[10px] text-red-600">{rowErrors.next_touch_at}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-full" data-testid="end-shift-cancel">
            Keep working
          </Button>
          <Button
            onClick={handleClose}
            disabled={saving}
            className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full"
            data-testid="close-shift-confirm"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {saving ? 'Closing\u2026' : 'Close shift'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
